import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, Upload, CheckCircle2, Loader2, Eye, AlertCircle } from "lucide-react";
import { BANK_ACCOUNTS } from "@/data/registrationConstants";

const STATUS_META = {
  pending:  { c: "bg-amber-100 text-amber-800", l: "Menunggu Verifikasi Admin" },
  verified: { c: "bg-emerald-100 text-emerald-800", l: "Diverifikasi" },
  rejected: { c: "bg-red-100 text-red-800", l: "Ditolak" },
};

const fmtIDR = (n) => `Rp ${new Intl.NumberFormat("id-ID").format(Math.round(n || 0))}`;

export default function PaymentProofSection({ booking, onUpdate }) {
  const [uploading, setUploading] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [amount, setAmount] = React.useState(booking.payment_proof_amount_idr || "");
  const [ref, setRef] = React.useState(booking.payment_proof_ref || "");
  const inputRef = React.useRef(null);

  const status = booking.payment_proof_status;
  const statusMeta = status ? STATUS_META[status] : null;
  const storagePath = booking.payment_proof_url;

  const upload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "application/pdf"].includes(file.type)) {
      toast.error("Format harus JPG, PNG, atau PDF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File terlalu besar (max 5 MB)");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const params = new URLSearchParams();
      if (amount) params.set("amount_idr", String(amount));
      if (ref) params.set("ref", ref);
      const r = await api.post(
        `/my-bookings/${booking.id}/payment-proof?${params.toString()}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Bukti pembayaran terupload — menunggu verifikasi admin");
      onUpdate(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload gagal");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const view = async () => {
    if (preview) { window.open(preview, "_blank"); return; }
    try {
      const r = await api.get(`/files/${storagePath}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPreview(url);
      window.open(url, "_blank");
    } catch {
      toast.error("Gagal membuka file");
    }
  };
  React.useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  return (
    <div className="mt-6 pt-6 border-t border-border" data-testid="payment-proof-section">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <p className="overline">Bukti Pembayaran</p>
        {statusMeta && (
          <span className={`text-[10px] font-medium uppercase tracking-widest rounded-full px-2.5 py-1 ${statusMeta.c}`} data-testid="payment-proof-status">
            {statusMeta.l}
          </span>
        )}
      </div>

      {/* Bank accounts */}
      <div className="rounded-lg bg-muted/40 border border-border p-4 mb-4">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Transfer ke rekening berikut</p>
        <div className="space-y-1.5 text-sm">
          {BANK_ACCOUNTS.map((b) => (
            <div key={b.number} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <div>
                <span className="font-semibold text-primary">{b.bank}</span>
                <span className="text-muted-foreground text-xs ml-2">a.n. {b.name}</span>
              </div>
              <span className="font-mono text-primary">{b.number}</span>
            </div>
          ))}
        </div>
      </div>

      {status === "rejected" && booking.payment_proof_reject_reason && (
        <div className="rounded-md bg-red-50 border-l-3 border-red-500 p-3 text-xs text-red-900 mb-4 flex gap-2" data-testid="payment-proof-reject-reason">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div><strong>Ditolak:</strong> {booking.payment_proof_reject_reason}</div>
        </div>
      )}

      {status === "verified" ? (
        <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="text-sm">
            <p className="text-emerald-900 font-semibold">Pembayaran terverifikasi</p>
            {booking.payment_proof_amount_idr ? (
              <p className="text-xs text-emerald-800">{fmtIDR(booking.payment_proof_amount_idr)} · Ref: {booking.payment_proof_ref || "—"}</p>
            ) : null}
          </div>
          {storagePath && (
            <button onClick={view} className="ml-auto text-xs px-3 py-2 rounded-full border border-emerald-300 hover:bg-emerald-100 inline-flex items-center gap-1.5">
              <Eye className="h-3 w-3" /> Lihat
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Jumlah Transfer</span>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Rp"
                data-testid="payment-proof-amount"
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-white text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">No. Ref / Catatan</span>
              <input
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                placeholder="Ref bank / berita transfer"
                data-testid="payment-proof-ref"
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-white text-sm"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <label className="text-sm px-4 py-2.5 rounded-full bg-primary text-white hover:bg-primary/90 cursor-pointer inline-flex items-center gap-2" data-testid="payment-proof-upload">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {storagePath ? "Ganti Bukti Transfer" : "Upload Bukti Transfer"}
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                disabled={uploading}
              />
            </label>
            {storagePath && (
              <button onClick={view} className="text-sm px-4 py-2 rounded-full border border-border hover:bg-muted inline-flex items-center gap-1.5" data-testid="payment-proof-view">
                <Eye className="h-3.5 w-3.5" /> Lihat file terupload
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Wallet className="h-3 w-3" /> Format: JPG/PNG/PDF · max 5 MB. Admin akan verifikasi dalam 1×24 jam.
          </p>
        </div>
      )}
    </div>
  );
}
